'use client'

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Button,
  Flex,
  FormLabel,
  IconButton,
  Input,
  Select,
  Spacer,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { ICoursesInPerson } from '../../../../interfaces/Website/Courses/CoursesInPerson/ICoursesInPerson'
import { CoursesOnlineService } from '../../../../services/CoursesOnline/CoursesOnlineService'
import CoursesOnlineModal from './CoursesOnlineModal'
import Paginator from '../../../../components/paginator'

export default function CoursesOnlinePage() {
  const service = new CoursesOnlineService()
  const cancelRef = useRef(null)
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [length, setLength] = useState(25)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState('')
  const [courses, setCourses] = useState<ICoursesInPerson[]>([])

  const [deleting, setDeleting] = useState<ICoursesInPerson | null>(null)

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  const [editing, setEditing] = useState<ICoursesInPerson | undefined>(undefined)

  const loadCourses = async () => {
    try {
      const { data, total_filtered } = await service.list(search, page, length)
      setCourses(data)
      setTotalPages(Math.ceil(total_filtered / length))
    } catch {
      toast({
        title: 'Erro ao carregar cursos.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  useEffect(() => {
    loadCourses()
  }, [])

  useEffect(() => {
    loadCourses()
  }, [length, page])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadCourses()
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [search])

  const handleDelete = async () => {
    if (!deleting) return

    try {
      await service.delete(deleting.id)

      toast({
        title: 'Excluído com sucesso',
        description: `O curso ${deleting.name} foi removido`,
        status: 'success',
        duration: 3000,
        isClosable: true
      })

      setCourses((prev) => prev.filter((item) => item.id !== deleting.id))
    } catch (error) {
      toast({
        title: 'Erro ao excluir',
        description: 'Não foi possível excluir o curso.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsDeleteOpen(false)
      setDeleting(null)
    }
  }

  const toggleStatus = async (course: ICoursesInPerson, enabled: boolean) => {
    try {
      await service.updateEnable(course.id, enabled)

      toast({
        title: 'Sucesso!',
        description: `O curso foi atualizado com sucesso`,
        status: 'success',
        duration: 3000,
        isClosable: true
      })

      setCourses((prev) =>
        prev.map((item) => {
          if (item.id === course.id) {
            item.enabled = enabled
          }
          return item
        })
      )
    } catch (error) {
      toast({
        title: 'Erro!',
        description: 'Não foi possível atualizar o curso.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  return (
    <>
      <Flex mb="4">
        <Flex flex="1" alignItems="center">
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            Mostrar
          </FormLabel>
          <Select
            size="sm"
            variant="outline"
            id="per-page"
            w="75px"
            mr="2"
            onChange={(e) => setLength(parseInt(e.target.value))}
            value={length}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            resultados por página
          </FormLabel>
        </Flex>
        <Spacer />
        <Flex flex="1" justifyContent="flex-end" alignItems="center">
          <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
            Pesquisar
          </FormLabel>
          <Input
            type="search"
            size="sm"
            variant="outline"
            id="search"
            w="200px"
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
        </Flex>
      </Flex>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Nome do curso</Th>
              <Th>Formatos disponíveis</Th>
              <Th>Áreas de Conhecimento</Th>
              <Th>Dinâmica do Curso</Th>
              <Th>Ativar/Inativar</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {courses.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  Nenhum curso encontrado
                </Td>
              </Tr>
            ) : (
              courses.map((course) => (
                <Tr key={course.id}>
                  <Td>{course.name}</Td>
                  <Td>
                    <Flex direction="row" gap="2">
                      <Badge colorScheme="blue">Online Gravado</Badge>
                    </Flex>
                  </Td>
                  <Td>{course.knowledge_areas?.map((a) => a.name).join(', ') || '-'}</Td>
                  <Td>{course.dynamics?.map((d) => d.name).join(', ') || '-'}</Td>
                  <Td>
                    <Switch
                      isChecked={course.enabled}
                      onChange={(e) => toggleStatus(course, e.target.checked)}
                      colorScheme="green"
                    />
                  </Td>
                  <Td>
                    <Tooltip label="Editar">
                      <IconButton
                        aria-label="Editar"
                        icon={<i className="lni lni-pencil" />}
                        colorScheme="blue"
                        size="sm"
                        mr={2}
                        onClick={() => {
                          setEditing(course)
                          onOpen()
                        }}
                      />
                    </Tooltip>
                    <Tooltip label="Excluir">
                      <IconButton
                        aria-label="Excluir"
                        icon={<i className="lni lni-trash-can" />}
                        size="sm"
                        colorScheme="red"
                        onClick={() => {
                          setDeleting(course)
                          setIsDeleteOpen(true)
                        }}
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
      />

      <Tooltip label="Adicionar curso" aria-label="Adicionar curso" placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={() => {
            onOpen()
          }}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>

      <CoursesOnlineModal
        onSuccess={loadCourses}
        editCourse={editing}
        isOpen={isOpen}
        onClose={() => {
          onClose()
          setEditing(undefined)
        }}
      />

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDeleteOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmar exclusão
            </AlertDialogHeader>

            <AlertDialogBody>
              Tem certeza que deseja remover o curso <strong>{deleting?.name}</strong>?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsDeleteOpen(false)}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}
